import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { useRef } from "react";
import { film } from "./film";

/** Act 1 opener - a poster, not a form. */
export default function HeroPoster({ onScrollToBrief }: { onScrollToBrief: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-24">
        <img
          src={film.hero}
          alt="A team walking a countryside path in late afternoon light"
          width={1920}
          height={1088}
          className="h-full w-full object-cover film-tone"
        />
        <div className="absolute inset-0 film-grain" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/45 to-primary/25" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative container mx-auto px-4 laptop:px-6 desktop:px-6 min-h-[100svh] flex flex-col justify-end pb-20 pt-32"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-xs uppercase tracking-[0.22em] text-primary-foreground/80 mb-7"
        >
          qwip offsites
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-funnel font-bold text-primary-foreground max-w-4xl text-4xl tablet:text-5xl laptop:text-[4.2rem] desktop:text-[4.8rem] leading-[1.02]"
        >
          your personal{" "}
          <span className="font-cormorant italic font-normal">team day out</span> planner.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-primary-foreground/85 text-lg"
        >
          one sentence about your team. three complete days back - venue, food, travel and an
          evening, all costed. built for teams spread across the country.
        </motion.p>

        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          onClick={onScrollToBrief}
          className="mt-14 inline-flex w-fit items-center gap-3 text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-foreground/40">
            <motion.span
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowDown size={15} />
            </motion.span>
          </span>
          write your brief
        </motion.button>
      </motion.div>
    </section>
  );
}
